/**
 * Application Routes
 *
 * Route guards (middleware) usage:
 *
 * 1. PUBLIC - Accessible by everyone (authenticated or not)
 *    <PublicGuard>
 *      <SomePage />
 *    </PublicGuard>
 *
 * 2. GUEST - Only non-authenticated users (redirects to home if logged in)
 *    <GuestGuard>
 *      <SignInPage />
 *    </GuestGuard>
 *
 * 3. AUTH - Only authenticated users (redirects to sign-in if not logged in)
 *    <AuthGuard>
 *      <DashboardPage />
 *    </AuthGuard>
 *
 * 4. ROLE - Only users with specific roles
 *    <RoleGuard allowedRoles={["admin", "manager"]}>
 *      <AdminPanel />
 *    </RoleGuard>
 */

import { lazy } from "react"
import { Navigate, Route, Routes } from "react-router-dom"
import { useUser } from "@/contexts/user-context"

function RootRedirect() {
  const { roles } = useUser()
  // Dashboard (Ana Sayfa) yalnızca IT_ADMIN'e açık; diğer herkes budget-plans'a düşer
  // Dashboard herkese açıldı, direk yönlendirme dashboarda yapıldı 12.06.2026 Pınar
  if (roles.includes("IT_ADMIN")) return <Navigate to="/roles" replace />
  return <Navigate to="/dashboards" replace />
}

import { AuthGuard, PublicGuard, RoleGuard } from "./guards"

// Layouts
const DashboardLayout = lazy(() => import("@/layouts/dashboard-layout"))
const PlainLayout = lazy(() => import("@/layouts/plain-layout"))

// Auth Pages
const AuthCallbackPage = lazy(() => import("@/pages/auth/callback"))

// Plain Layout Pages
const MaintenancePage = lazy(() => import("@/pages/maintenance"))
const NotFound404Page = lazy(() => import("@/pages/not-found-404"))
const Unauthorized401Page = lazy(() => import("@/pages/unauthorized-401"))
const NoAccessPage = lazy(() => import("@/pages/no-access"))

// Dashboard Pages
const DashboardsPage = lazy(() => import("@/pages/dashboards"))

// Account Pages
const ProfilePage = lazy(() => import("@/pages/account/profile"))
const SettingsPage = lazy(() => import("@/pages/account/settings"))
const NotificationsSettingsPage = lazy(() => import("@/pages/account/settings/notifications"))
const SecuritySettingsPage = lazy(() => import("@/pages/account/settings/security"))
const PlanBillingSettingsPage = lazy(() => import("@/pages/account/settings/plan-and-billing"))

// Management Pages
const TreeTablePage = lazy(() => import("@/pages/tree-table"))
const ParametersPage = lazy(() => import("@/pages/parameters"))
const MessagesPage = lazy(() => import("@/pages/messages"))
const RolesPage = lazy(() => import("@/pages/roles"))
const OrgUnitsPage = lazy(() => import("@/pages/org-units"))
const OrgUnitRelationsPage = lazy(() => import("@/pages/org-units/components/org-unit-relations-table"))
const OrgUnitRoleUsersPage = lazy(() => import("@/pages/org-units/components/org-unit-role-users-table"))
const OrgUnitTemplatesPage = lazy(() => import("@/pages/org-units/components/org-unit-templates-page"))
const BudgetItemsPage = lazy(() => import("@/pages/budget-items"))
const BudgetItemRulesPage = lazy(() => import("@/pages/budget-items/components/budget-item-rules-page"))
const BudgetTemplatesPage = lazy(() => import("@/pages/budget-templates"))
const BudgetTemplateItemsPage = lazy(() => import("@/pages/budget-templates/components/budget-template-items-page"))
const BudgetPlansPage = lazy(() => import("@/pages/budget-plans"))
const BudgetPlanReportsPage = lazy(() => import("@/pages/budget-plan-reports"))
const BudgetPlanReportsDetailPage = lazy(() => import("@/pages/budget-plan-reports/components/budget-plan-reports-detail-page"))
const BudgetApprovalTrackingPage = lazy(() => import("@/pages/budget-approval-tracking"))

export function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* ============================================= */}
      {/* GUEST + PUBLIC ROUTES (Plain Layout) */}
      {/* ============================================= */}
      <Route element={<PlainLayout />}>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        <Route
          path="/maintenance"
          element={
            <PublicGuard>
              <MaintenancePage />
            </PublicGuard>
          }
        />
        <Route
          path="/not-found-404"
          element={
            <PublicGuard>
              <NotFound404Page />
            </PublicGuard>
          }
        />
        <Route
          path="/unauthorized-401"
          element={
            <PublicGuard>
              <Unauthorized401Page />
            </PublicGuard>
          }
        />
        <Route
          path="/no-access"
          element={
            <PublicGuard>
              <NoAccessPage />
            </PublicGuard>
          }
        />
      </Route>

      {/* ============================================= */}
      {/* AUTH ROUTES - Only for authenticated users */}
      {/* ============================================= */}
      <Route
        element={
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        }
      >
        {/* Dashboards — yalnızca IT_ADMIN; yetkisi yoksa budget-plans'a yönlendir; Pınar 12.06.2026 APK_ADMIN, USER, APPROVER  açıldı*/}
        <Route path="/dashboards" element={<RoleGuard allowedRoles={["APK_ADMIN", "USER", "APPROVER"]} redirectTo="/budget-plans"><DashboardsPage /></RoleGuard>} />

        {/* Account */}
        <Route path="/account/profile" element={<ProfilePage />} />
        <Route path="/account/settings" element={<SettingsPage />} />
        <Route path="/account/settings/notifications" element={<NotificationsSettingsPage />} />
        <Route path="/account/settings/security" element={<SecuritySettingsPage />} />
        <Route path="/account/settings/plan-and-billing" element={<PlanBillingSettingsPage />} />

        {/* Management */}
        <Route path="/parameters" element={<RoleGuard allowedRoles={["IT_ADMIN"]}><ParametersPage /></RoleGuard>} />
        <Route path="/messages" element={<RoleGuard allowedRoles={["IT_ADMIN"]}><MessagesPage /></RoleGuard>} />
        <Route path="/roles" element={<RoleGuard allowedRoles={["IT_ADMIN"]}><RolesPage /></RoleGuard>} />
        <Route path="/organizations" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><OrgUnitsPage /></RoleGuard>} />
        <Route path="/organizations/:orgId/relations" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><OrgUnitRelationsPage /></RoleGuard>} />
        <Route path="/organizations/:orgId/role-users" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><OrgUnitRoleUsersPage /></RoleGuard>} />
        <Route path="/organizations/:orgId/templates" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><OrgUnitTemplatesPage /></RoleGuard>} />
        <Route path="/tree-table" element={<TreeTablePage />} />
        <Route path="/budget-items" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><BudgetItemsPage /></RoleGuard>} />
        <Route path="/budget-items/:itemId/rules" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><BudgetItemRulesPage /></RoleGuard>} />
        <Route path="/budget-templates" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><BudgetTemplatesPage /></RoleGuard>} />
        <Route path="/budget-templates/:tempId/items" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><BudgetTemplateItemsPage /></RoleGuard>} />
        <Route path="/budget-plans" element={<RoleGuard allowedRoles={["APK_ADMIN", "USER", "APPROVER"]}><BudgetPlansPage /></RoleGuard>} />
        <Route path="/budget-plan-reports" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><BudgetPlanReportsPage /></RoleGuard>} />
        <Route path="/budget-plan-reports/detail" element={<RoleGuard allowedRoles={["APK_ADMIN"]}><BudgetPlanReportsDetailPage /></RoleGuard>} />
        <Route path="/budget-approval-tracking" element={<RoleGuard allowedRoles={["APK_ADMIN", "USER", "APPROVER"]}><BudgetApprovalTrackingPage /></RoleGuard>} />
      </Route>

      {/* 404 - Not Found */}
      <Route path="/not-found" element={<NotFound404Page />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
